import React, { useRef, useMemo } from 'react';
import DataGrid, { Column, Paging, ColumnFixing } from 'devextreme-react/data-grid';
import { Button } from 'devextreme-react/button';
import { Tooltip } from 'devextreme-react/tooltip';
import AntLocalization from '../../utils/AntLocalization';
import AntSelectBox from '../AntSelectBox';
import AntAPI from '../../utils/AntAPI';

// Preview grid cho Stage 3 (AI Voucher Analyzer) — UI y chang AntImportPreviewGrid:
//   • Cột # (row index)
//   • Cột error icon + tooltip
//   • Cột dữ liệu theo mapping (Item field → ai column)
//   • Cột Remove (đổi skip_flag = true thay vì xoá hẳn, vì BE giữ lịch sử)
// Khác AntImport: data nguồn là analysis.proposed_items (mỗi row có .fields + .errors
// + .match_status + .skip_flag); validate đã chạy ở BE qua actionValidate, FE chỉ render.

function fieldLabel(cnf, field, fallback) {
    if (!cnf) return fallback || field;
    var f = cnf.getCnfField ? cnf.getCnfField(field) : null;
    if (f && f.label) return AntLocalization.translateText(f.label);
    return fallback || field;
}
function fieldDataType(cnf, field) {
    if (!cnf) return 'string';
    var f = cnf.getCnfField ? cnf.getCnfField(field) : null;
    return (f && f.dataType) || 'string';
}

var MONEY_PATTERN = /(_price|_amt|_amount|_value|_revenue|_cost|_fee|_tax(?!_pct))$/;
var MONEY_EXACT = { unit_price: 1, price: 1, total_price: 1, net_price: 1, gross_price: 1 };
function isMoneyField(field) {
    if (!field) return false;
    if (MONEY_EXACT[field]) return true;
    return MONEY_PATTERN.test(field);
}

export default function Stage3PreviewGrid(props) {
    var items = props.items || [];
    var mapping = props.mapping || {};
    var cnf = props.config;
    var grid = useRef();
    var expenseAPI = useMemo(function () { return props.isExpense ? new AntAPI('exp', 'EXPItem') : null; }, [props.isExpense]);

    // Build cols (giống AntImportPreviewGrid.load build cols từ mappedFields).
    var cols = useMemo(function () {
        var enabledFields = Object.keys(mapping).filter(function (k) { return !!mapping[k]; });
        return enabledFields.map(function (field) {
            var dt = fieldDataType(cnf, field);
            var col = {
                name: field,
                dataField: field,
                caption: fieldLabel(cnf, field, field),
                alignment: 'left',
                format: undefined
            };
            if (isMoneyField(field) || dt === 'currency') {
                col.format = AntLocalization.getCurrencyFormat();
                col.alignment = 'right';
            } else if (dt === 'float' || dt === 'int' || dt === 'number') {
                col.format = '#,##0.##';
                col.alignment = 'right';
            }
            return col;
        });
    }, [mapping, cnf]);

    // Build rows: flatten .fields + đính kèm errors, activity. Loại bỏ skip_flag
    // rows khỏi DISPLAY (giống AntImport xoá hẳn khỏi grid sau khi click X) — vẫn
    // giữ trong items state để BE biết user đã bỏ qua các dòng nào.
    var rows = useMemo(function () {
        var out = [];
        items.forEach(function (it, idx) {
            if (it && it.skip_flag) return; // đã bỏ qua → ẩn khỏi grid
            var f = (it && it.fields) || {};
            var row = Object.assign({}, f);
            row.row_index = idx;
            row.errors = (it && it.errors) || [];
            row.activity = (it && it.match_status === 'Resolved') ? 'Update' : 'Create';
            row.match_status = it && it.match_status;
            row._idx = idx;
            out.push(row);
        });
        return out;
    }, [items]);

    var renderExpenseCategory = function (cell) {
        var row = cell.data;
        return <AntSelectBox
            key={row.row_index + ':' + (row.item_code || '')}
            API={expenseAPI}
            dataField="item_code"
            isRemote={true}
            searchConditions={{ is_active: 1 }}
            defaultValue={row.item_code || null}
            showClearButton={true}
            options={{ table: 'EXPItem', keyExpr: 'code', valueExpr: 'code', displayExpr: 'name' }}
            onValueChanged={function (event) {
                if (!props.onChange || event.value === row.item_code) return;
                props.onChange(items.map(function (item, index) {
                    if (index !== row._idx) return item;
                    return { ...item, fields: { ...item.fields, item_code: event.value || null,
                        item_name: event.rowData ? event.rowData.name : null } };
                }));
            }}
        />;
    };

    var onRemove = function (data) {
        // Set skip_flag (giống xoá khỏi UI nhưng BE vẫn giữ history) → row biến
        // mất khỏi grid lập tức (useMemo lọc skip_flag).
        if (!props.onChange) return;
        var idx = data._idx;
        var next = items.map(function (it, i) {
            if (i !== idx) return it;
            return Object.assign({}, it, { skip_flag: true });
        });
        props.onChange(next);
    };

    // RENDER y chang AntImportPreviewGrid.
    var renderErrorIcon = function (info) {
        var data = info.data;
        var hasErr = (data.errors && data.errors.length > 0) || (data.match_status && data.match_status !== 'Resolved');
        if (!hasErr) {
            var idOk = 'aiva-ok-' + data.row_index;
            return (
                <div key={idOk}>
                    <Button
                        className="ant-grid-icon ant-icon-valid"
                        id={idOk}
                        type="normal"
                        stylingMode="text"
                        focusStateEnabled={false}
                        hoverStateEnabled={false}
                        activeStateEnabled={false}
                    >
                        <i className={'dx-icon dx-icon-check'}></i>
                    </Button>
                </div>
            );
        }
        var msgs = (data.errors || []).map(function (e) { return (e && (e.error || e.message)) || String(e); });
        if (data.match_status && data.match_status !== 'Resolved') {
            msgs.push(AntLocalization.translateText({
                vi: 'Chưa khớp danh mục: ' + data.match_status,
                en: 'Unmatched: ' + data.match_status,
                fr: 'Hors référentiel : ',
                es: 'Fuera de catálogo: ',
                id: 'Belum ada di master: ',
                ja: 'マスタ未一致: ',
                zh: '未匹配主数据: ',
                ko: '마스터 불일치: ',
                hi: 'मास्टर में नहीं: ',
                ar: 'غير مطابق للقائمة: '
            }));
        }
        var errors = msgs.filter(Boolean).join('\n').split('\n');
        var id = 'aiva-err-' + data.row_index;
        return (
            <div key={id}>
                <Button
                    className="ant-grid-icon ant-icon-error"
                    id={id}
                    type="normal"
                    stylingMode="text"
                    focusStateEnabled={false}
                    hoverStateEnabled={false}
                    activeStateEnabled={false}
                >
                    <i className={'dx-icon dx-icon-info'}></i>
                </Button>
                <Tooltip
                    target={`#${id}`}
                    className="ant-tooltip aiva-err-tip"
                    hideEvent="mouseleave"
                    showEvent={{ name: 'mouseenter dxclick keyup' }}
                    hideOnOutsideClick={true}
                >
                    <div>
                        {errors.map(function (item, key) {
                            if (key < errors.length - 1)
                                return (
                                    <React.Fragment key={key}>
                                        <p>{item}</p>
                                        <br />
                                    </React.Fragment>
                                );
                            return <p key={key}>{item}</p>;
                        })}
                    </div>
                </Tooltip>
            </div>
        );
    };

    var renderRemoveButton = function (info) {
        var data = info.data;
        var id = 'aiva-rm-' + data.row_index;
        return (
            <div>
                <Button
                    className="ant-grid-icon"
                    id={id}
                    onClick={function () { onRemove(data); }}
                    type="normal"
                    stylingMode="text"
                    focusStateEnabled={false}
                    hoverStateEnabled={false}
                >
                    <i className="dx-icon icon dx-icon-close"></i>
                </Button>
            </div>
        );
    };

    // Y CHANG AntImport (line 547-550): khi có row lỗi → CHỈ hiển thị các
    // row lỗi để user focus fix; khi không còn lỗi → show all. Cùng pattern
    // `errosRows.length > 0 ? errosRows : gridData.rows`.
    var errorRows = rows.filter(function (r) {
        return (r.errors && r.errors.length > 0) || (r.match_status && r.match_status !== 'Resolved');
    });
    var dataRows = errorRows.length > 0 ? errorRows : rows;

    var noActivityGroup = props.noActivityGroup === true;

    return (
        <React.Fragment>
        <DataGrid
            ref={grid}
            noDataText={AntLocalization.translateText({ vi: 'Không có dữ liệu !', en: 'Have no data !', fr: 'Aucune donnée !', es: '¡Sin datos!', id: 'Tidak ada data!', ja: 'データがありません！', zh: '没有数据！', ko: '데이터 없음!', hi: 'कोई डेटा नहीं!', ar: 'لا توجد بيانات!' })}
            className={'dx-card wide-card aiva-preview-grid'}
            dataSource={dataRows}
            keyExpr="row_index"
            focusedRowEnabled={false}
            columnAutoWidth={true}
            columnHidingEnabled={false}
            showColumnLines={false}
            showRowLines={true}
            showBorders={true}
            rowAlternationEnabled={true}
            repaintChangesOnly={true}
            height={'100%'}
            pager={{
                showInfo: true,
                infoText: '{2} ' + AntLocalization.translateText({ vi: 'dòng', en: 'rows', fr: 'lignes', es: 'filas', id: 'baris', ja: '行', zh: '行', ko: '행', hi: 'पंक्तियाँ', ar: 'صفوف' }),
                visible: true
            }}
            sorting={{
                clearText: AntLocalization.translateText({ vi: 'Bỏ sắp xếp', en: 'Remove sorting', fr: 'Annuler le tri', es: 'Quitar orden', id: 'Hapus urutan', ja: '並べ替え解除', zh: '取消排序', ko: '정렬 해제', hi: 'क्रम हटाएँ', ar: 'إلغاء الترتيب' }),
                ascendingText: 'A->Z',
                descendingText: 'Z->A'
            }}
            grouping={{
                autoExpandAll: true,
                expandMode: 'rowClick',
                texts: { groupContinuedMessage: '', groupContinuesMessage: '' }
            }}
            summary={{
                groupItems: [{
                    column: 'activity',
                    summaryType: 'count',
                    displayFormat: '{0} ' + AntLocalization.translateText({ vi: 'dòng', en: 'rows', fr: 'lignes', es: 'filas', id: 'baris', ja: '行', zh: '行', ko: '행', hi: 'पंक्तियाँ', ar: 'صفوف' }),
                    showInColumn: 'error'
                }]
            }}
        >
            <Paging defaultPageSize={props.defaultPageSize ? props.defaultPageSize : 10} />
            <ColumnFixing enabled={true} />

            <Column
                name="remove"
                caption=" "
                width={40}
                alignment="center"
                cellRender={renderRemoveButton}
                fixed={true}
                fixedPosition="left"
                allowSorting={false}
            />
            <Column
                name="row_index"
                dataField="row_index"
                caption="#"
                width={36}
                alignment="center"
                fixed={true}
                fixedPosition="left"
                calculateCellValue={function (data) { return parseInt(data.row_index, 10) + 1; }}
            />
            <Column
                name="error"
                caption=" "
                width={32}
                alignment="center"
                cellRender={renderErrorIcon}
                fixed={true}
                fixedPosition="left"
                allowSorting={false}
            />

            {!noActivityGroup && (
                <Column
                    name="activity"
                    dataField="activity"
                    caption={AntLocalization.translateText({ vi: 'Tác vụ', en: 'Activity', fr: 'Activité', es: 'Actividad', id: 'Aktivitas', ja: '操作', zh: '活动', ko: '활동', hi: 'गतिविधि', ar: 'نشاط' })}
                    fixed={true}
                    fixedPosition="left"
                    alignment="left"
                    calculateCellValue={function (data) {
                        if (data.activity === 'Create') return AntLocalization.translateText({ vi: 'Tạo mới', en: 'Create New', fr: 'Créer nouveau', es: 'Crear nuevo', id: 'Buat Baru', ja: '新規作成', zh: '新建', ko: '새로 만들기', hi: 'नया बनाएं', ar: 'إنشاء جديد' });
                        if (data.activity === 'Update') return AntLocalization.translateText({ vi: 'Cập nhật', en: 'Update', fr: 'Mettre à jour', es: 'Actualizar', id: 'Perbarui', ja: '更新', zh: '更新', ko: '업데이트', hi: 'अपडेट करें', ar: 'تحديث' });
                        return data.activity;
                    }}
                    groupIndex={0}
                />
            )}

            {cols
                .filter(function (col) {
                    // Giống AntImport: chỉ render cột nào có ít nhất 1 row data
                    // khác empty/null — tránh hiển thị cột trống choán không gian.
                    if (props.isExpense && col.dataField === 'item_code') return true;
                    return dataRows.some(function (r) {
                        return r[col.dataField] !== undefined && r[col.dataField] !== null && r[col.dataField] !== '';
                    });
                })
                .map(function (col) {
                    return (
                        <Column
                            key={col.dataField}
                            name={col.name}
                            caption={col.caption}
                            dataField={col.dataField}
                            width={col.width}
                            alignment={col.alignment || 'left'}
                            fixed={col.fixed}
                            format={col.format}
                            cellRender={props.isExpense && col.dataField === 'item_code' ? renderExpenseCategory : undefined}
                            minWidth={props.isExpense && col.dataField === 'item_code' ? 260 : undefined}
                        />
                    );
                })}
        </DataGrid>
        </React.Fragment>
    );
}
